import React, { useId, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Collapse, Stack, Typography } from '@mui/material'
import { ArrowLeftIcon } from 'lucide-react'
import { Button } from '@repo/ui'

import { resolveReturnTo, useVeraSpaceStore } from 'stores'
import { useVeraSpaceMutationsQuery } from 'hooks/api'
import { DisclosureTextButton } from 'components/input/DisclosureTextButton'
import { storage } from 'utils'
import { permissionAreas } from 'utils/permissionLabels'
import { spaceGrantHeadline, spaceGrantSummary } from 'utils/veraSupportAccess'

/**
 * VERA-876: Shown when a Vera staffer is operating inside a company's space.
 *
 * Displays the company name and a "Return to Internal" button. Clicking
 * return POSTs the best-effort exit-space audit log, clears the space store,
 * clears selectedCompany from storage, and navigates back to the internal page
 * the staffer came from.
 *
 * VRG-369: that used to be hardcoded to /internal/companies, so entering a demo's
 * space from the Demos console and coming back landed you on a different list.
 * The origin is now recorded at `enter()` time as `returnTo`. `resolveReturnTo`
 * maps it onto an allowlist of internal LIST routes — a stored detail page could
 * point at a record purged while in-space, and landing on a row that no longer
 * exists is worse than landing on the list.
 *
 * VRG-836: it spans the WHOLE WINDOW, above the sidebar — Layout renders it as the first
 * child of the app's column, so the sidebar and the page both sit below it. It used to be
 * the first child of `main`, which started it at the sidebar's right edge and so left the
 * sidebar outside the bar. The sidebar is the nav of the brand whose space you entered,
 * which makes it part of the space rather than a neighbour of it. It is still invisible
 * when no space session is active.
 *
 * VRG-836: and it is a THEME surface now — light grey in light mode, the same zinc-800 it
 * always was in dark. The old note here claimed a fixed hex was needed because "a mode
 * banner should read the same in light and dark". The first half of that was a real
 * constraint (the brand grey ramp inverts for dark mode, so `grey.900` renders LIGHT and a
 * naive token flips the bar inside out); the conclusion did not follow. A near-black bar
 * is the loudest thing on a light page, which inverts the hierarchy on a surface whose job
 * is ambient context rather than alarm — the same argument §32 makes about permanently-red
 * destructive triggers. `grey.100` is the one rung that is light grey in light mode and
 * zinc-800 in dark, so the dark appearance is unchanged and the light one is calm. See
 * `BANNER_SURFACE` for the per-mode values.
 *
 * VRG-677: it also says WHAT this brand lets Vera support see, from the grant captured
 * at `enter()` (the same list `useCanAccess` checks in space). Most brands have granted
 * nothing, and before this a staffer met a page of 403s with nothing on screen saying
 * that the cause was the brand's grant, or who could change it. The role it names is
 * "Vera support" — the column's name in the brand's Roles & permissions matrix — not the
 * "Vera Manager role" this banner used to cite, which no brand admin could find.
 *
 * VRG-677 (compact): the full grant sentence wrapped to ~8 lines with the Ask panel open,
 * pushing every page down by a third of the viewport. The banner now shows ONE line
 * (`spaceGrantHeadline`: the scope plus at most two areas, then "+N more") and puts the
 * full sentence, including where the brand changes it, behind "Show details".
 */
/**
 * The bar's surface and ink, stated per mode rather than left implicit in a ramp (VRG-836).
 *
 * Every value is a theme token, and the two rows below are what those tokens resolve to —
 * written out because the brand neutral ramps are REVERSED for dark mode (`darkNeutral` in
 * `packages/ui/theme/theme.ts`), so `grey.100` is the light-grey rung in one mode and the
 * zinc-800 rung in the other. That inversion is exactly what makes one token correct for
 * both, and exactly what makes it unreadable without this note:
 *
 *   light   bg hsla(200,6%,90%)  ink rgba(0,0,0,0.87)  muted rgba(0,0,0,0.6)
 *   dark    bg #27272a           ink #fafafa           muted #a1a1aa
 *
 * `#27272a` is the fixed hex this component used to carry for both modes, so dark mode is
 * byte-identical to what shipped.
 *
 * `text.secondary` replaces a hand-written `rgba(255,255,255,0.72)`, which was the same
 * "white ink on a dark bar" assumption one layer down and would have been dark-grey-on-
 * light-grey here.
 */
const BANNER_SURFACE = {
  bg: 'grey.100',
  ink: 'text.primary',
  muted: 'text.secondary',
} as const

export const VeraSpaceBanner = () => {
  const navigate = useNavigate()

  const companyId = useVeraSpaceStore((s) => s.companyId)
  const companyName = useVeraSpaceStore((s) => s.companyName)
  const returnTo = useVeraSpaceStore((s) => s.returnTo)
  const permissions = useVeraSpaceStore((s) => s.permissions)
  const exitSpace = useVeraSpaceStore((s) => s.exit)

  const { exitAsync } = useVeraSpaceMutationsQuery()
  // Hooks before the early return (Rules of Hooks).
  const [detailsOpen, setDetailsOpen] = useState(false)
  const detailsId = useId()

  if (!companyId) return null

  const { areas, viewOnly } = permissionAreas(permissions)
  const grantInput = { areas, viewOnly, grantSize: permissions.length }
  const grantHeadline = spaceGrantHeadline(grantInput)
  const grantSummary = spaceGrantSummary(grantInput)
  const spaceLabel = `You are in ${companyName ? `${companyName}'s` : 'a company'} space as Vera support.`

  // Resolved BEFORE `exitSpace()` clears the store — reading it after would always
  // see null and always fall back to Companies, which is the bug this replaces.
  const destination = resolveReturnTo(returnTo)

  const handleReturn = () => {
    // Fire the best-effort audit while still in-space (X-Vera-Space still set), but
    // do NOT await it — the UI exit must be synchronous and can never hang on the
    // audit request. Awaiting it previously left the banner stuck when the request
    // stalled (store never cleared, no navigation).
    exitAsync({ companyId }).catch(() => {
      // ignore — audit is best-effort
    })
    // `to` is the VRG-677 `leaving` handoff: the guards that bounce internal staff off a
    // brand page render BEFORE this navigate commits, and now bounce to the same place.
    exitSpace({ to: destination })
    storage.removeItem('selectedCompany')
    navigate(destination)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 4,
        py: 1.5,
        // See BANNER_SURFACE for what these resolve to in each mode, and why one token
        // covers both.
        bgcolor: BANNER_SURFACE.bg,
        color: BANNER_SURFACE.ink,
        gap: 2,
        // VRG-836 — it is the first child of Layout's app COLUMN now, so this is what
        // keeps the bar at its content height and hands the whole remainder to the row
        // holding the sidebar and the page.
        flexShrink: 0,
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" fontWeight={500} noWrap title={spaceLabel}>
          {spaceLabel}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={3} sx={{ mt: 0.5, minWidth: 0 }}>
          <Typography
            variant="body3"
            noWrap
            data-testid="vera-space-grant-summary"
            sx={{ minWidth: 0, color: BANNER_SURFACE.muted }}
          >
            {grantHeadline}
          </Typography>
          <Box sx={{ flexShrink: 0 }}>
            <DisclosureTextButton
              expanded={detailsOpen}
              onToggle={() => setDetailsOpen((open) => !open)}
              label={{ expanded: 'Hide details', collapsed: 'Show details' }}
              controls={detailsId}
            />
          </Box>
        </Stack>
        <Collapse in={detailsOpen}>
          <Typography
            id={detailsId}
            variant="body3"
            data-testid="vera-space-grant-details"
            sx={{ display: 'block', mt: 1, maxWidth: 720, color: BANNER_SURFACE.muted, lineHeight: 1.5 }}
          >
            {grantSummary}
          </Typography>
        </Collapse>
      </Box>
      <Button
        variant="secondary"
        onClick={handleReturn}
        startIcon={<ArrowLeftIcon size={14} strokeWidth={1.5} />}
        sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
      >
        Return to Internal
      </Button>
    </Box>
  )
}
